import { ProfilePlanModel, WageTypeEnum } from "@payping/api";
import { getUserPanelData } from "@payping/redux/actions";
import { Trans } from "@payping/utils/localeUtils";
import { Typography, useMessagedAsync, useMessagedAsyncFn } from "@payping/web-components";
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from "jalali-moment";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { Plan } from "./types";
import { PlanButton } from "./PlanButton";
import { PlanButtonSub } from "./PlanButtonSub";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { selectAtom } from "jotai/utils";
import { useAtom } from "jotai";
import { dashboardPreferencesAtom } from "@/core/state";

const languageAtom = selectAtom(dashboardPreferencesAtom, (val) => val.language);

function isAgreement(plan: ProfilePlanModel) {
  return plan.id === 5;
}

export const usePlans = (plansResponse?: ProfilePlanModel[], deps?: any[]): Plan[] => {
  const [language] = useAtom(languageAtom);

  const currentPlanId = useAppSelector((state) => state.user.panelInfo?.currentPlan?.id);
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();

  const [{ loading: loadingDetail }, retryGetPlanData] = useMessagedAsyncFn(
    async () => {
      await dispatch(getUserPanelData());
    },
    [dispatch],
    undefined,
    { useProgress: true },
  );

  useEffect(() => {
    retryGetPlanData();
  }, [retryGetPlanData]);

  const getPlanSpecifics = (plan: ProfilePlanModel) => {
    // TODO: This switch is a technical debt & needs to be moved to backend
    switch (plan.id) {
      case 0:
        return { features: ["panel", "notifs", "other", "fakeFeature", "fakeFeature"], translationKey: "basic" };
      case 5:
        return { features: [], translationKey: "premium" };
      case 6:
        return { features: ["panel", "notifs", "other", "specials", "fakeFeature"], translationKey: "standard" };
      case 7:
        return { features: ["panel", "notifs", "other", "specials", "platform"], translationKey: "professional" };
      default:
        return { features: [], translationKey: "otherPlan" };
    }
  };

  const { value: planList } = useMessagedAsync(
    async () => {
      const planListResult: Plan[] = [
        ...(plansResponse || [])
          .sort((a, b) => (a.index && b.index && a.index > b.index ? 1 : -1))
          .map((plan) => ({
            ...plan,
            ...getPlanSpecifics(plan),
            button: <PlanButton plan={plan} loadingDetail={loadingDetail} retryGetPlanData={retryGetPlanData} />,
            buttonSub: <PlanButtonSub plan={plan} />,
            premium: isAgreement(plan),
            priceDescription: isAgreement(plan)
              ? ""
              : `${plan.discountedPrice ? "" : "0 "}${t("toman")} ${
                  plan.durationDays ? moment.duration(plan.durationDays, "days").locale(language).humanize(true) : ""
                }`,
            // eslint-disable-next-line no-nested-ternary
            priceText: isAgreement(plan)
              ? (plan.title ?? "")
              : !plan.primaryPrice
                ? t("upgrade.free")
                : formatLocalizedNumber({ value: plan.primaryPrice }),
            refund:
              plan.primaryPrice && plan.primaryPrice > 0 ? (
                <Typography.Text>{t(`upgrade.guarantee`)}</Typography.Text>
              ) : (
                <Typography.Text>&nbsp;</Typography.Text>
              ),
            wage: isAgreement(plan) ? (
              t("upgrade.plans.agreement.buttonSubscript")
            ) : (
              <Trans
                i18nKey={
                  plan.wageType === WageTypeEnum.Percentage
                    ? "User:upgrade.plans.wagePercent"
                    : "User:upgrade.plans.wageFixed"
                }
                values={{
                  currency: t("toman"),
                  maximumWage: formatLocalizedNumber({ value: plan.maximumWage ?? 0 }),
                  subject: plan?.isWageByPayerSupported ? t("upgrade.plans.payer") : t("upgrade.plans.receiver"),
                  wageValue: formatLocalizedNumber({ value: plan.wageValue ?? 0 }),
                }}
                components={{ b: <b className="color--gray700" /> }}
              />
            ),
            title: isAgreement(plan) ? "" : plan.title,
          })),
      ];

      return planListResult;
    },
    [...(deps || []), currentPlanId, loadingDetail, retryGetPlanData, plansResponse],
    { useProgress: true },
  );

  return planList || [];
};
